<!--
/**
 * Webapp header template
 *
 * @package ARS Webapp
 * @author René Schulze <schulze@thadeus-roth.de>
 */
 -->

<script setup>
    import { reactive, ref, nextTick } from 'vue';
    import { t } from '?/Composable/Translation';
    import config from '?/Config';
    import store from '?/Stores';
    import { focusElement } from '?/Composable/Tools';
    import { mainMenu } from '?/ProjectMenus';

    // Data
    const state = reactive({
        index: null,
        menuIsActive: false,
        mainMenu: mainMenu,
        keyboardEventCallback: null
    });

    const menuTogglerRef = ref();

    // Filter visibility
    state.mainMenu = mainMenu.filter(item => {
        return (item.visible);
    });

    // Methods
    const toggleMenu = () => {
        state.menuIsActive = !state.menuIsActive;

        // Focus the first menu entry
        if (state.menuIsActive) {
            nextTick(() => {
                state.mainMenu[0].ref.querySelector('a').focus();
                state.index = 0;
                registerKeyboardInput();
            });
        // Focus button
        } else {
            menuTogglerRef.value.focus();
            state.index = null;
            unRegisterKeyboardInput();
        }
    };

    const closeMenu = () => {
        state.menuIsActive = false;
        state.index = null;

        unRegisterKeyboardInput();
    };

    const handleKeyBoardInput = (event) => {
        const key = event.key;
        const oldIndex = state.index;
        const prevIndex = ((state.index - 1) >= 0)
            ? state.index - 1
            : state.mainMenu.length - 1;
        const nextIndex = ((state.index + 1) <= (state.mainMenu.length - 1))
            ? state.index + 1
            : 0;

        if (key == 'Escape' || (event.shiftKey && key == 'Tab')) {
            if (state.menuIsActive) {
                event.preventDefault();
                focusElement(menuTogglerRef.value);
                closeMenu();
            }

            return;
        }

        if (key == 'Tab') {
            if (state.menuIsActive) {
                event.preventDefault();
                focusElement(menuTogglerRef.value, 'next');
                closeMenu();
            }

            return;
        }

        if (key == 'ArrowDown' || key == 'ArrowRight') {
            event.preventDefault();
            state.index = nextIndex;
        }

        if (key == 'ArrowUp' || key == 'ArrowLeft') {
            event.preventDefault();
            state.index = prevIndex;
        }

        if (
            state.index !== null &&
            state.index !== oldIndex &&
            state.menuIsActive
        ) {
            state.mainMenu[state.index].ref.querySelector('a').focus();
        }
    };

    const registerKeyboardInput = () => {
        document.addEventListener(
            'keydown',
            handleKeyBoardInput
        );
    };

    const unRegisterKeyboardInput = () => {
        document.removeEventListener(
            'keydown',
            handleKeyBoardInput
        );
    };
</script>

<template>
    <div class="header"
         :class="{ 'header-navigation-active': state.menuIsActive }"
         v-if="store.getters.isAuthenticated">

        <div class="header-logo" v-if="config.projectName">
            <router-link
                class="header-logo-link"
                to="/webapp">
                {{ config.projectName }}
            </router-link>
        </div>

        <h1 class="header-headline" v-html="$route.meta.title"></h1>

        <nav id="header-navigation"
             class="header-navigation"
             :class="{ 'active': state.menuIsActive }"
             :aria-label="$t('webapp.header-menu')">

             <button class="header-navigation-toggler"
                     :class="(state.menuIsActive) ? 'close' : 'open'"
                     aria-controls="header-navigation-list"
                     :aria-expanded="(state.menuIsActive).toString()"
                     @click.prevent="toggleMenu()"
                     ref="menuTogglerRef">
                 <span class="header-navigation-toggler-label open"
                       v-if="!state.menuIsActive">
                    {{ $t('webapp.open-menu') }}
                </span>
                <span class="header-navigation-toggler-label close"
                          v-if="state.menuIsActive">
                       {{ $t('webapp.close-menu') }}
                </span>
            </button>

            <div class="header-navigation-content">
                <ul id="header-navigation-list"
                    class="header-navigation-list">

                    <template v-for="(item, index) in state.mainMenu">
                        <li class="header-navigation-item"
                            :class="item.classes"
                            :ref="el => { state.mainMenu[index].ref = el }">
                            <router-link
                                class="header-navigation-link"
                                :tabindex="(!state.menuIsActive) ? 0 : -1"
                                :to="item.to"
                                @click="state.menuIsActive = false">
                                {{ item.name }}
                            </router-link>
                        </li>
                    </template>

                </ul>
            </div>
        </nav>

        <div class="header-news"
             :class="{ 'unseen-news': store.getters.unseenNews }">
            <router-link to="/webapp/">
                <span class="header-news-label">{{ $t('webapp.news') }}</span>
            </router-link>
        </div>
     </div>
</template>

<style>
    .header-navigation-content {

        display: none;

    }

    .active .header-navigation-content {

        display: block;

    }
</style>
